import Box from 'UIKit/Box/Box'
import Image from 'UIKit/Image'
import { HEIGHT_ROW } from 'UIKit/Table/TableHistory'
import Text from 'UIKit/Text'
import TokenLogo from 'UIKit/TokenLogo'
import BigNumber from 'bignumber.js'
import { DISPLAY_AMOUNT } from 'config/constants'
import { Token } from 'config/types'
import { BetDetails } from 'config/types/profile'
import useGameTag from 'hooks/useGameTag'
import useOpenBetDetails from 'hooks/useOpenBetDetails'
import Column from 'layout/Components/Column'
import { RowCell, RowMiddle } from 'layout/Components/Row'
import React, { useCallback } from 'react'
import { useUserSettings } from 'state/profile/hooks'
import styled from 'styled-components'
import theme from 'theme'
import { colors } from 'theme/colors'
import { getFullDisplayBalance } from 'utils/formatBalance'
import TokenAmountFiatDisplay from 'views/Fiat/TokenAmountFiatDisplay'
import InhouseGameIcons from 'views/InhouseGame/components/InhouseGameIcons'
import { DisplayMultiplier } from 'views/Profile/Transactions/components/styled'
import UserDisplay from 'views/Profile/components/UserDisplay'
import { LIVE_BET_ANIMATION_DURATION } from '../config'

const CurrencyAmount: React.FC<{
  token: Token
  value: string
  color?: string
}> = ({ token, value, color = theme.colors.text }) => {
  const {
    userSettings: { isDisplayInFiat },
  } = useUserSettings()

  return (
    <RowMiddle>
      <Box minWidth="24px">
        <TokenLogo token={token} size={20} networkImageProps={{ width: 12, height: 12 }} />
      </Box>
      <Column ml="8px">
        <RowValueText color={color} fontWeight={500} lineHeight="14px">
          {isDisplayInFiat ? (
            <TokenAmountFiatDisplay
              tokenAmount={{
                token,
                amount: BigNumber(value),
              }}
            />
          ) : (
            getFullDisplayBalance(BigNumber(value), 0, DISPLAY_AMOUNT)
          )}
        </RowValueText>
      </Column>
    </RowMiddle>
  )
}

const LiveBetRow: React.FC<{
  betDetails: BetDetails
  className?: string
  animationRef: {
    disabled?: boolean
  }
}> = ({ betDetails: { bet, player, payout, multiplier, game, betId }, className, animationRef }) => {
  const gameTag = useGameTag(game.tag)
  const { presentBetModal } = useOpenBetDetails()
  const formatMultiplier = new BigNumber(multiplier)

  const onModalDetails = useCallback(() => {
    presentBetModal(betId)
  }, [betId, presentBetModal])

  return (
    <StyledRow className={`${className} ${animationRef.disabled ? 'no-animation' : ''}`} onClick={onModalDetails}>
      <RowCell width={['40%', '', '30%', '', '']} minWidth={40}>
        <Box p="0px 8px" width={'100%'}>
          <RowMiddle>
            <Box minWidth={20} height={20} width={20}>
              {game?.inhouseGameType ? (
                <InhouseGameIcons type={game?.inhouseGameType} fill={colors.textSubtle} />
              ) : (
                <StyledImage src={gameTag?.img} width={20} height={20} alt="game-tag" />
              )}
            </Box>
            <GameNameWrapper>
              <RowValueText ml="8px">{game.name}</RowValueText>
            </GameNameWrapper>
          </RowMiddle>
        </Box>
      </RowCell>

      <RowCell width={['20%', '', '', '', '30%']}>
        <Box p="0px 8px" width={'100%'}>
          <StyledUserDisplay user={player} />
        </Box>
      </RowCell>

      <RowCell width={['0%', '', '25%', '', '']} display={['none', '', 'flex']}>
        <Box p="0px 8px" width={'100%'}>
          <CurrencyAmount token={bet.token} value={bet.amount} />
        </Box>
      </RowCell>

      <RowCell width={['0%', '', '', '', '15%']} display={['none', '', '', '', 'flex']}>
        <Box p="0px 8px" width={'100%'}>
          <RowValueText>
            <DisplayMultiplier amount={formatMultiplier} color={formatMultiplier.gt(0) ? 'success' : 'textAlt1'} />
          </RowValueText>
        </Box>
      </RowCell>

      <RowCell width={['40%', '', '25%', '', '20%']}>
        <Box p="0px 8px" width={'100%'}>
          <CurrencyAmount
            token={payout.token}
            value={payout.amount}
            color={new BigNumber(payout.amount).gt(0) ? 'success' : 'textAlt1'}
          />
        </Box>
      </RowCell>
    </StyledRow>
  )
}

const StyledRow = styled(Box)`
  display: flex;
  flex-direction: row;
  width: 100%;
  height: ${HEIGHT_ROW}px;
  padding: 0 16px;
  cursor: pointer;

  &:not(.no-animation) {
    animation: new-bet-appear ${LIVE_BET_ANIMATION_DURATION / 1000}s;
  }

  &.active {
    background: rgba(255, 255, 255, 0.04);
  }

  @keyframes new-bet-appear {
    0% {
      margin-top: -${HEIGHT_ROW}px;
    }
    100% {
      margin-top: 0px;
    }
  }
`

const RowValueText = styled(Text)`
  font-size: 14px;
  font-weight: 500;
  overflow: hidden;
  text-overflow: ellipsis;
  word-break: break-all;
`

const StyledUserDisplay = styled(UserDisplay)`
  ${Text} {
    display: none;
  }
  font-size: 14px;

  max-width: 90%;
  ${({ theme }) => theme.mediaQueries.lg} {
    ${Text} {
      display: block;
      overflow: hidden;
      white-space: nowrap;
      text-overflow: ellipsis;
    }
  }
`

const GameNameWrapper = styled(Box)`
  max-width: 80%;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
`

const StyledImage = styled(Image)`
  img {
    filter: grayscale(1) invert(1);
  }
`

export default React.memo(LiveBetRow)
